import React, { useEffect, useState } from "react";
import { api, fmtUSD } from "@/lib/api";
import { Zap, TrendingUp, Flame, Coins, Copy, Check, Wallet } from "lucide-react";
import { toast } from "sonner";
import QuotronGallery from "@/components/QuotronGallery";

export default function Treasury() {
  const [t, setT] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { api.get("/treasury").then((r) => setT(r.data)).catch(() => {}); }, []);
  if (!t) return <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 text-stone-500">lighting the fire…</div>;

  const copyWallet = async () => {
    try {
      await navigator.clipboard.writeText(t.treasury_wallet);
      setCopied(true);
      toast.success("treasury address copied");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("copy failed"); }
  };

  const quotronsTotal = (t.quotrons_tier1 || 0) + (t.quotrons_tier2 || 0) + (t.quotrons_tier3 || 0);
  const yieldTotal = (t.nvda_yield_usd || 0) + (t.tsla_yield_usd || 0) + (t.aapl_yield_usd || 0) + (t.spy_yield_usd || 0);
  const sellable = t.sellable_usd || 0;

  const stocks = [
    { k: "NVDA", v: t.nvda_yield_usd || 0, color: "#4FFF4F" },
    { k: "TSLA", v: t.tsla_yield_usd || 0, color: "#FFAB00" },
    { k: "AAPL", v: t.aapl_yield_usd || 0, color: "#FFD54F" },
    { k: "SPY",  v: t.spy_yield_usd || 0, color: "#E04E3B" },
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <div className="text-xs uppercase tracking-widest text-[#FFAB00]">treasury transparency</div>
      <div className="mt-2 flex items-center flex-wrap gap-3">
        <h1 className="font-display text-5xl sm:text-6xl font-black">receipts, <span className="ember-text">not promises</span></h1>
      </div>
      <div className="mt-4 inline-flex items-center gap-2 rounded-md border border-[#FFAB00]/40 bg-[#141008] px-3 py-2 text-sm text-stone-200" data-testid="fee-callout">
        <Coins className="w-4 h-4 text-[#FFC940]" />
        <span className="mono text-xs">
          3% transaction fee on <span className="text-[#FFC940]">$QUODUST</span> on Pons → buys & hardwires new Quotrons
        </span>
      </div>

      {/* ONE WALLET banner */}
      <section
        data-testid="one-wallet-banner"
        className="mt-6 rounded-xl border border-[#FFAB00]/50 bg-[#141008] p-6 ember-glow"
      >
        <div className="flex items-start gap-3 flex-wrap">
          <Wallet className="w-5 h-5 text-[#FFC940] mt-1" />
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-[#FFAB00]">one wallet · all assets</div>
            <div className="font-display text-3xl sm:text-4xl mt-1">everything lives at this address</div>
            <div className="mt-1 text-xs text-stone-400">dust donations · 3% Pons fees · hardwired Quotrons — all held here, all public</div>
            <div className="mt-4 flex items-center gap-2 rounded-md border border-[#FFAB00]/40 bg-[#0A0906] p-3">
              <code className="font-mono text-sm sm:text-base break-all text-[#FFC940] flex-1" data-testid="one-wallet-address">
                {t.treasury_wallet}
              </code>
              <button
                onClick={copyWallet}
                data-testid="one-wallet-copy"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#FFAB00] hover:bg-[#FFC940] text-[#0A0906] font-semibold text-xs"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "copied" : "copy"}
              </button>
            </div>
            <div className="mt-4 grid sm:grid-cols-3 gap-2">
              <Chip label="donations received" value={fmtUSD(t.sellable_usd)} hint={`${t.sellable_count} tx`} />
              <Chip label="pons fees collected" value={fmtUSD(t.pons_lifetime_fee_usd)} hint={`${fmtUSD(t.pons_lifetime_volume_usd)} lifetime volume`} />
              <Chip label="quotron nfts held" value={t.quotrons_count} hint="Robinhood Chain · chain id 4663" />
            </div>
          </div>
        </div>
      </section>

      <QuotronGallery />

      <div className="mt-6 grid sm:grid-cols-3 gap-3">
        <Stat icon={Zap} label="Quotrons owned" value={quotronsTotal} hint={`T1 ${t.quotrons_tier1} · T2 ${t.quotrons_tier2} · T3 ${t.quotrons_tier3}`} testId="treasury-quotrons" />
        <Stat icon={TrendingUp} label="Monthly yield" value={fmtUSD(yieldTotal)} hint="across all tracks" testId="treasury-yield-month" />
        <Stat icon={Flame} label="QUODUST distributed" value={fmtUSD(t.quodust_distributed_usd)} hint="lifetime to holders" testId="treasury-distributed" />
      </div>

      <section className="mt-6 grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-[#3A2E1C] bg-[#141008] p-5">
          <div className="font-display text-2xl mb-4">yield per stock track</div>
          <div className="space-y-3">
            {stocks.map((s) => {
              const pct = yieldTotal > 0 ? (s.v / yieldTotal) * 100 : 0;
              return (
                <div key={s.k}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />{s.k}</span>
                    <span className="mono text-stone-400">{fmtUSD(s.v)} · {pct.toFixed(1)}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-[#1E1810] overflow-hidden">
                    <div className="h-full" style={{ width: `${pct}%`, backgroundColor: s.color, boxShadow: `0 0 10px ${s.color}80` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-xl border border-[#3A2E1C] bg-[#141008] p-5">
          <div className="font-display text-2xl mb-3">dust converted</div>
          <div className="text-xs text-stone-500">lifetime cremation receipts</div>
          <div className="mt-4 rounded-md border border-[#FFAB00]/40 bg-[#0A0906] p-4 ember-glow">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">sellable value swept</div>
            <div className="font-display text-3xl ember-text">{fmtUSD(sellable)}</div>
            <div className="mt-2 text-xs text-stone-500">{t.sellable_count || 0} tx routed into Quotrons</div>
          </div>
          {t.note && <p className="mt-4 text-xs text-stone-500">{t.note}</p>}
        </div>
      </section>

      <section className="mt-6 rounded-xl border border-[#3A2E1C] bg-[#141008] p-5">
        <div className="font-display text-2xl mb-2">treasury wallets</div>
        <div className="text-xs text-stone-500 mb-4">All EVM chains share one address · Solana address published separately</div>
        <div className="rounded-md border border-[#FFAB00]/40 bg-[#0A0906] p-4 ember-glow">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">EVM treasury</div>
          <div className="font-mono text-sm break-all text-[#FFC940]">{t.treasury_wallet}</div>
        </div>
      </section>
    </div>
  );
}

function Chip({ label, value, hint }) {
  return (
    <div className="rounded-md border border-[#3A2E1C] bg-[#0A0906] p-3">
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className="mt-1 font-display text-xl ember-text">{value}</div>
      {hint && <div className="mt-0.5 text-[10px] text-stone-500 mono">{hint}</div>}
    </div>
  );
}

function Stat({ icon: Icon, label, value, hint, testId }) {
  return (
    <div data-testid={testId} className="rounded-xl border border-[#3A2E1C] bg-[#141008] p-5 hover:border-[#FFAB00]/50 transition-all">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest text-stone-500">{label}</span>
        <Icon className="w-4 h-4 text-[#FFAB00]" />
      </div>
      <div className="mt-3 font-display text-4xl font-bold ember-text">{value}</div>
      {hint && <div className="mt-1 text-xs text-stone-500">{hint}</div>}
    </div>
  );
}
